// @ts-nocheck
import React, { useEffect, useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { RouteComponentProps } from 'react-router-dom';
import queryString from 'query-string';
import classNames from 'classnames';
import {
  AddIcon,
  Avatar,
  AvatarSkeleton,
  Button,
  Input,
  PageHeader,
  Pagination,
  SearchIcon,
  Table,
} from '@gamesb42/ui-kit';

import MainContent from 'components/MainContent';
import urls from 'constants/urls';
import { getUsers, getUsersArchive, getUsersArchiveWithRoles, getUsersWithRoles } from 'api/users';
import { UserKeyEnum, User } from 'types/UserTypes';

import styles from './styles.module.scss';
import Flex from 'components/Flex';
import ActionButton from './ActionButton';

const GAME_BOOST42_ID = '2df533a0-6f77-46d3-b047-ae7cb85143ff';

export interface QueryParams {
  size?: number;
  page?: number;
  isArchive?: string;
  name?: string;
  roles?: any;
}

interface UsersData {
  total: number;
  items: User[];
}

const Users = ({
  history: {
    push,
    replace,
    location: { search, pathname },
  },
}: RouteComponentProps) => {
  const { t } = useTranslation();
  const [usersData, setUsersData] = useState<UsersData>({
    items: [],
    total: 0,
  });
  const [loading, setLoading] = useState(true);
  const query: QueryParams = queryString.parse(search, { parseNumbers: true });
  const { page = 1, size = 30, isArchive = 'false', roles = [] } = query;
  const [isChecked, setIsChecked] = useState(isArchive === 'true');
  const [filter, setFilter] = useState({ name: '', company: '' });

  const [render, setRender] = useState(0);
  const updateData = () => {
    setRender((prevVal) => prevVal + 1);
  };

  const onChangeQuery = useCallback(
    (newValues: Partial<QueryParams>) => {
      let queryParams = {
        ...newValues,
        isArchive: isChecked ? true : false,
      };

      if (filter.name.length > 0) queryParams = { ...queryParams, name: filter.name };
      if (filter.company.length > 0) queryParams = { ...queryParams, company: filter.company };

      replace(`${pathname}?${queryString.stringify(queryParams, { skipEmptyString: true })}&roles=client-admin`);
    },
    [pathname, replace, isChecked],
  );

  const columns = [
    {
      title: t('users.userName'),
      dataIndex: UserKeyEnum.FIRST_NAME,
      skeletonRender: <AvatarSkeleton />,
      render: (firstName: string, row: User) => (
        <Avatar name={firstName} surname={row[UserKeyEnum.LAST_NAME]} email={row[UserKeyEnum.EMAIL]} />
      ),
    },
    {
      title: 'Company',
      dataIndex: UserKeyEnum.COMPANY_NAME,
      skeletonProps: {
        width: '78px',
        type: 'text14' as const,
      },
      render: (companyName: string, row: User) => {
        const otherNames = (row[UserKeyEnum.OTHER_COMPANIES] ?? [])
          .map((el) => el?.company?.legal_name || el?.company?.name)
          .filter(Boolean) as string[];
        const allNames = [companyName, ...otherNames].filter(Boolean) as string[];

        if (allNames.length === 0) return null;

        const isGameBoost = row[UserKeyEnum.COMPANY_ID] === GAME_BOOST42_ID;
        const primary = isGameBoost ? (
          <span className={styles.gameBoost42}>{allNames[0]}</span>
        ) : (
          allNames[0]
        );

        return (
          <span title={allNames.join(', ')}>
            {primary}
            {allNames.length > 1 && (
              <span style={{ color: '#686F88', marginLeft: 4 }}>+{allNames.length - 1}</span>
            )}
          </span>
        );
      },
    },
  ];

  useEffect(() => {
    let params = {
      page,
      size,
      isArchive,
    };

    if (filter.name.length > 0) params = { ...params, name: filter.name };
    if (filter.company.length > 0) params = { ...params, company: filter.company };

    const searchParams = new URLSearchParams(Object.entries(params));
    const queryString = searchParams.toString() + `&roles=client-admin`;

    setLoading(true);
    isArchive === 'true'
      ? getUsersArchiveWithRoles(queryString)
          .then((res) => setUsersData({ items: res.items, total: res.total }))
          .catch(console.error)
          .finally(() => setLoading(false))
      : getUsersWithRoles(queryString)
          .then((res) => setUsersData({ items: res.items, total: res.total }))
          .catch(console.error)
          .finally(() => setLoading(false));
  }, [page, size, isArchive, filter, render]);

  useEffect(() => {
    const handleUpdate = () => updateData();
    window.addEventListener('updateParent', handleUpdate);

    return () => {
      window.removeEventListener('updateParent', handleUpdate);
    };
  }, []);

  return (
    <MainContent title={t('users.users')} type="flexibleLimit" flexColumn>
      <PageHeader
        title={t('users.users')}
        rightContent={
          <span className={styles.rightContent}>
            <Button type="primary" icon={<AddIcon size={16} />} onClick={() => push(urls.getCreateUser())}>
              {t('users.inviteUser')}
            </Button>
          </span>
        }
      />

      <Flex gap="20px" style={{ marginBottom: '12px' }}>
        <div style={{ flexGrow: 1 }}>
          <Input
            label="Admin name or email"
            suffix={<SearchIcon />}
            style={{ color: '#686F88' }}
            value={filter.name}
            onChange={(e) => setFilter({ ...filter, name: e.target.value })}
          />
        </div>
        <div style={{ flexGrow: 1 }}>
          <Input
            label="Company"
            suffix={<SearchIcon />}
            style={{ color: '#686F88' }}
            value={filter.company}
            onChange={(e) => setFilter({ ...filter, company: e.target.value })}
          />
        </div>
        <ActionButton isChecked={isChecked} setIsChecked={setIsChecked} />
      </Flex>

      <Table
        columns={columns}
        loading={loading}
        loadingRowCount={30}
        dataSource={usersData.items}
        className={styles.table}
        rowKey={UserKeyEnum.ID}
        rowClassName={(row) => classNames(styles.row, row.status === 'deleted' && styles.deleted)}
        onRow={(record) => {
          return {
            onClick: () => {
              if (isArchive && isArchive === 'false') push(`${urls.getUsers()}/${record[UserKeyEnum.ID]}`);
            },
          };
        }}
      />
      <Pagination
        total={usersData.total}
        current={page}
        pageSize={size}
        onChange={(newCurrent: number, newPageSize: number) => onChangeQuery({ page: newCurrent, size: newPageSize })}
      />
    </MainContent>
  );
};

export default Users;
